'use client';

import React from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CrownIcon } from '@/components/ui/crown-icon';
import { X, Copy, Mail, Check } from 'lucide-react';

interface MobileInviteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inviteUrl: string;
  onCopyLink: () => void;
  copied: boolean;
  isHost?: boolean;
}

export function MobileInviteModal({
  open,
  onOpenChange,
  inviteUrl,
  onCopyLink,
  copied,
  isHost = false,
}: MobileInviteModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="md:hidden h-screen w-screen max-w-none max-h-none rounded-none p-0 bg-black border-0"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">Convide mais pessoas</DialogTitle>
        <div className="h-full flex flex-col bg-black">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-neutral-700">
            <h2 className="text-xl font-semibold text-white">Convide mais pessoas</h2>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-neutral-800"
              onClick={() => onOpenChange(false)}
            >
              <X className="w-6 h-6" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 px-6 py-8 space-y-12">
            {/* Host indicator */}
            {isHost && (
              <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className="bg-yellow-500 text-black px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                    <CrownIcon className="text-black" size={14} /> Anfitrião
                  </div>
                </div>
                <p className="text-yellow-200 text-sm text-center">
                  Você está entrando como anfitrião. Compartilhe o link abaixo com os convidados.
                </p>
              </div>
            )}

            {/* Compartilhando o link da reunião */}
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-white text-center">
                {isHost ? 'Compartilhe o link para convidados' : 'Compartilhando o link da reunião'}
              </h3>

              <Button
                onClick={onCopyLink}
                className={`w-full py-6 text-lg font-medium rounded-xl transition-colors duration-200 ${
                  copied
                    ? 'bg-green-600 hover:bg-green-700 text-white cursor-default'
                    : 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                }`}
              >
                {copied ? <Check className="w-5 h-5 mr-3" /> : <Copy className="w-5 h-5 mr-3" />}
                {copied ? 'Link copiado!' : 'Copiar link da reunião'}
              </Button>
            </div>

            {/* Compartilhar convite da reunião */}
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-white text-center">
                Compartilhar convite da reunião
              </h3>

              <div className="flex justify-center gap-4 px-4">
                {/* Copy/Share */}
                <Button
                  variant="outline"
                  size="lg"
                  className={`w-14 h-14 rounded-full flex-shrink-0 transition-colors duration-200 ${
                    copied
                      ? 'bg-green-600 border-green-500 text-white hover:bg-green-700 cursor-default'
                      : 'bg-neutral-800 border-neutral-600 text-white hover:bg-neutral-700 cursor-pointer'
                  }`}
                  onClick={onCopyLink}
                >
                  {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </Button>

                {/* Email */}
                <a
                  href={`mailto:?subject=${encodeURIComponent('Convite para reunião')}&body=${encodeURIComponent('Entre na reunião: ' + inviteUrl)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-shrink-0"
                >
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-14 h-14 rounded-full bg-neutral-800 border-neutral-600 text-white hover:bg-neutral-700"
                  >
                    <Mail className="w-5 h-5" />
                  </Button>
                </a>

                {/* Google */}
                <a
                  href={`https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent('Convite para reunião')}&body=${encodeURIComponent('Entre na reunião: ' + inviteUrl)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-shrink-0"
                >
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-14 h-14 rounded-full bg-neutral-800 border-neutral-600 text-white hover:bg-neutral-700 text-lg font-bold"
                  >
                    G
                  </Button>
                </a>

                {/* Outlook */}
                <a
                  href={`https://outlook.live.com/mail/deeplink/compose?subject=${encodeURIComponent('Convite para reunião')}&body=${encodeURIComponent('Entre na reunião: ' + inviteUrl)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-shrink-0"
                >
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-14 h-14 rounded-full bg-neutral-800 border-neutral-600 text-white hover:bg-neutral-700 text-lg font-bold"
                  >
                    O
                  </Button>
                </a>

                {/* Yahoo */}
                <a
                  href={`https://compose.mail.yahoo.com/?subject=${encodeURIComponent('Convite para reunião')}&body=${encodeURIComponent('Entre na reunião: ' + inviteUrl)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-shrink-0"
                >
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-14 h-14 rounded-full bg-neutral-800 border-neutral-600 text-white hover:bg-neutral-700 text-lg font-bold"
                  >
                    Y
                  </Button>
                </a>
              </div>
            </div>

            {/* Separator */}
            <div className="border-t border-neutral-700 mt-8"></div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
